import { StateParams, StateProvider } from "@uirouter/angularjs";
import {
  ProgressiveDefinitionViewController,
  HtmlTemplate as ProgressiveDefinitionViewHtmlTemplate,
} from "./views/definitions";
import {
  ProgressiveControllerTypeViewController,
  HtmlTemplate as ProgressiveControllerTypeViewHtmlTemplate,
} from "./views/controllers";

import {
  ProgressiveMeterReadingSettingsViewController,
  HtmlTemplate as ProgressiveMeterReadingSettingsViewHtmlTemplate,
} from "./views/meterReadingSettings";
import {
  ProgressiveMeterReadingValidationSettingViewController,
  HtmlTemplate as ProgressiveMeterReadingValidationSettingViewHtmlTemplate,
} from "./views/validationSettings";

import {
  DailyReadingsViewController,
  HtmlTemplate as DailyReadingsViewHtmlTemplate,
} from "./views/dailyReadings";

import {
  DailyReadingsReadingsViewController,
  HtmlTemplate as DailyReadingsReadingsViewHtmlTemplate,
} from "./views/dailyReadings/readings";
import {
  DailyReadingsMachinesViewController,
  HtmlTemplate as DailyReadingsMachinesViewHtmlTemplate,
} from "./views/dailyReadings/machines";

import { vsComponentName as DailyReadingsGraphViewComponentName } from "./views/dailyReadings/graph";

import {
  ProgressiveDefinitionDetailViewController,
  HtmlTemplate as ProgressiveDefinitionDetailViewHtmlTemplate,
} from "./views/definitions/detail";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../records/server/serverUtility";
import {
  ProgressiveDefinitionEditViewController,
  HtmlTemplate as ProgressiveDefinitionEditViewHtmlTemplate,
} from "./views/definitions/edit";
import {
  DefinitionsDetailMachineViewController as ProgressiveDefinitionDetailMachineViewController,
  HtmlTemplate as ProgressiveDefinitionDetailMachineViewHtmlTemplate,
} from "./views/definitions/detail/machines";
import {
  ProgressiveDefinitionDetailReadingsViewController,
  HtmlTemplate as ProgressiveDefinitionDetailReadingsHtmlTemplate,
} from "./views/definitions/detail/readings";
import {
  ProgressiveDefinitionsDetailMachineEditViewController,
  HtmlTemplate as ProgressiveDefinitionsDetailMachineEditViewHtmlTemplate,
} from "./views/definitions/detail/machines/edit";
import { Record } from "../records/server/crud";
import {
  ProgressiveControllerTypeEditViewController,
  HtmlTemplate as ProgressiveControllerTypeEditViewHtmlTemplate,
} from "./views/controllers/add";
import {
  ProgressiveMeterReadingSettingsEditViewController,
  HtmlTemplate as ProgressiveMeterReadingSettingsEditViewHtmlTemplate,
} from "./views/meterReadingSettings/add";
import {
  RecordsUtilsService,
  vsName as RecordsUtilsServiceName,
} from "../records/utils";
import { cloneDeep } from "lodash";
import { IHttpService } from "angular";
import { IQueryResponse } from "../records/server/query";
import { ProgressivesContext } from "./views/model/progressivesContext";

type ReportGroup = {
  Name: string;
  Reports: Report[];
};

type Report = {
  ContentType: string;
  Default: boolean;
  Description: string;
  Extension: string;
  Id: string;
  JsReportId: string;
};

progressiveRoutes.$inject = ["$stateProvider"];
function progressiveRoutes($stateProvider: StateProvider) {
  const basePath = "main.machines.progressives";

  const progressiveDefinitionsPath = `${basePath}.progressiveDefinitions`;
  const progressiveDefinitionsDetailPath = `${progressiveDefinitionsPath}.detail`;
  const progressiveMeterReadingSettingsPath = `${basePath}.meterReadingSettings`;
  const progressiveControllersPath = `${basePath}.controllers`;
  const progressiveDailyReadingPath = `${basePath}.progressiveDailyReading`;
  const progressiveReadingValidationSettingsPath = `${basePath}.progressiveReadingValidationSettings`;

  const progressiveDefinitionsApiUrl =
    "/api/machines/progressives/ProgressiveDefinition";

  $stateProvider
    .state(basePath, {
      url: "/progressives",
      template: "<ui-view></ui-view>",
      abstract: true,
      resolve: {
        date: [
          "$stateParams",
          "environment",
          ($stateParams: StateParams, environment: { Date: Date }) =>
            $stateParams.date || environment.Date,
        ],
        progressivesContext: [
          "date",
          function (date: Date): ProgressivesContext {
            return {
              date,
            };
          },
        ],
        progressiveReport: [
          "$http",
          function ($http: IHttpService) {
            return $http({
              method: "get",
              url: "/api/reporting/progressives",
            }).then((result) => {
              const progressiveReports = <ReportGroup>(<unknown>result);
              return progressiveReports.Reports.find(
                (pr) => pr.Id === "configuration"
              );
            });
          },
        ],
      },
    })
    .state(progressiveDefinitionsPath, {
      url: "",
      template: ProgressiveDefinitionViewHtmlTemplate,
      controller: ProgressiveDefinitionViewController,
      controllerAs: "stateController",
    })
    .state(`${progressiveDefinitionsPath}.add`, {
      url: "/add",
      controller: ProgressiveDefinitionEditViewController,
      controllerAs: "stateController",
      template: ProgressiveDefinitionEditViewHtmlTemplate,
      resolve: {
        progressiveDefinition: [
          "$stateParams",
          RecordsUtilsServiceName,
          function (
            $stateParams: StateParams,
            recordUtils: RecordsUtilsService
          ) {
            const progressiveDefinition = cloneDeep(
              $stateParams.progressiveDefinition
            );
            recordUtils.stripIds(progressiveDefinition);
            return progressiveDefinition;
          },
        ],
      },
      params: {
        progressiveDefinition: {
          value: {},
        },
      },
    })
    .state(progressiveDefinitionsDetailPath, {
      url: "/{id:int}",
      controller: ProgressiveDefinitionDetailViewController,
      controllerAs: "stateController",
      template: ProgressiveDefinitionDetailViewHtmlTemplate,
      resolve: {
        progressiveDefinition: [
          ServerUtilityName,
          "$stateParams",
          function (serverUtility: ServerUtility, $stateParams: StateParams) {
            const id = $stateParams["id"];
            return serverUtility.getById({
              url: progressiveDefinitionsApiUrl,
              id,
            });
          },
        ],
      },
    })
    .state(`${progressiveDefinitionsDetailPath}.edit`, {
      url: "/edit",
      controller: ProgressiveDefinitionEditViewController,
      controllerAs: "stateController",
      template: ProgressiveDefinitionEditViewHtmlTemplate,
    })
    .state(`${progressiveDefinitionsDetailPath}.machines`, {
      url: "/machines",
      views: {
        detail: {
          controller: ProgressiveDefinitionDetailMachineViewController,
          controllerAs: "stateController",
          template: ProgressiveDefinitionDetailMachineViewHtmlTemplate,
        },
      },
    })
    .state(`${progressiveDefinitionsDetailPath}.machines.edit`, {
      url: "/edit",
      resolve: {
        currentProgressiveDefinitionMachines: [
          ServerUtilityName,
          "progressiveDefinition",
          function (
            serverUtility: ServerUtility,
            progressiveDefinition: Record
          ) {
            const ProgressiveDefinitionId = progressiveDefinition.Id;
            return serverUtility.fetchAll<Record>({
              url: `/api/machines/progressives/ProgressiveDefinition/${ProgressiveDefinitionId}/Machines`,
              query: {
                Fields: "Id,MachineConfigurationId",
              },
            });
          },
        ],
      },
      views: {
        "detail@^.^": {
          controller: ProgressiveDefinitionsDetailMachineEditViewController,
          controllerAs: "stateController",
          template: ProgressiveDefinitionsDetailMachineEditViewHtmlTemplate,
        },
      },
    })
    .state(`${progressiveDefinitionsDetailPath}.readings`, {
      url: "/readings",
      views: {
        detail: {
          controller: ProgressiveDefinitionDetailReadingsViewController,
          controllerAs: "stateController",
          template: ProgressiveDefinitionDetailReadingsHtmlTemplate,
        },
      },
    })
    .state(progressiveControllersPath, {
      url: "/controllers",
      template: ProgressiveControllerTypeViewHtmlTemplate,
      controller: ProgressiveControllerTypeViewController,
      controllerAs: "stateController",
    })
    .state(`${progressiveControllersPath}.add`, {
      url: "/add",
      template: ProgressiveControllerTypeEditViewHtmlTemplate,
      controller: ProgressiveControllerTypeEditViewController,
      controllerAs: "stateController",
    })
    .state(progressiveMeterReadingSettingsPath, {
      url: "/meterReadingSettings",
      template: ProgressiveMeterReadingSettingsViewHtmlTemplate,
      controller: ProgressiveMeterReadingSettingsViewController,
      controllerAs: "stateController",
    })
    .state(`${progressiveMeterReadingSettingsPath}.add`, {
      url: "/add",
      template: ProgressiveMeterReadingSettingsEditViewHtmlTemplate,
      controller: ProgressiveMeterReadingSettingsEditViewController,
      controllerAs: "stateController",
    })
    .state(progressiveReadingValidationSettingsPath, {
      url: "/validationSettings",
      template: ProgressiveMeterReadingValidationSettingViewHtmlTemplate,
      controller: ProgressiveMeterReadingValidationSettingViewController,
      controllerAs: "stateController",
    })
    .state(progressiveDailyReadingPath, {
      url: "/dailyReadings",
      template: DailyReadingsViewHtmlTemplate,
      controller: DailyReadingsViewController,
      controllerAs: "stateController",
    })
    .state(`${progressiveDailyReadingPath}.dailyReadings`, {
      url: "/readings",
      controller: DailyReadingsReadingsViewController,
      controllerAs: "stateController",
      template: DailyReadingsReadingsViewHtmlTemplate,
      resolve: {
        validationSettings: [
          ServerUtilityName,
          (serverUtility: ServerUtility) =>
            serverUtility
              .query({
                url: "/api/machines/progressives/ProgressiveMeterReadingValidationSetting",
                query: {
                  Take: 1,
                },
              })
              .then((queryResponse: IQueryResponse<any>) => {
                const validationSettings = queryResponse.Results.at(0);
                return validationSettings;
              }),
        ],
      },
    })
    .state(`${progressiveDailyReadingPath}.dailyMachines`, {
      url: "/machines",
      controller: DailyReadingsMachinesViewController,
      controllerAs: "stateController",
      template: DailyReadingsMachinesViewHtmlTemplate,
    })
    .state(`${progressiveDailyReadingPath}.readingGraph`, {
      url: "/graph",
      component: DailyReadingsGraphViewComponentName,
    });
}
export { progressiveRoutes as routes };
